import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { Alert, Checkbox, Divider, Form, Typography } from 'antd';
import dayjs from 'dayjs';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { GET_ACCOUNT_CARD_LAST_4 } from 'queries/account';
import { SETTLEMENTS_CADENCE_VALUES } from 'utilities/constants';
import { getEftaText } from 'utilities/helpers';

import PrecoScheduleTable from './PrecoScheduleTable';

const { Item } = Form;
const { Text, Title, Paragraph } = Typography;

const PrecoEFTAForm = ({
  form,
  onFinish,
  fundingAccountLast4,
  cadence,
  schedule,
  children,
}) => {
  const { customerId, accountId } = useParams();
  const { customerInfo } = useCustomerInfo();

  const cardLastFourQuery = useQuery(GET_ACCOUNT_CARD_LAST_4, {
    variables: { customerId, accountId },
  });

  const cardLast4 = cardLastFourQuery.data?.account.cardDetails.last4 || '';
  const customerName = customerInfo.fullName;
  const email = customerInfo.email;
  const today = dayjs().format('L');

  const authorizedSchedule = (schedule ?? [])
    .filter((payment) => payment.changeType !== 'delete')
    .sort((a, b) => dayjs(a.date) - dayjs(b.date));

  const lumpSumPayment =
    cadence === SETTLEMENTS_CADENCE_VALUES.ONE_TIME ||
    authorizedSchedule.length === 1;
  const paymentNoun = lumpSumPayment ? 'a payment' : 'payments';

  const authorizationText = getEftaText({
    eftaDetails: { cardLast4, customerName, fundingAccountLast4, email },
    todaysDate: today,
    paymentPlanSchedule: authorizedSchedule.map(({ amount, date }) => ({
      amount,
      date,
    })),
    cadence,
  });

  return (
    <QueryBoundary query={cardLastFourQuery}>
      <Form
        form={form}
        layout="vertical"
        onFinish={() => onFinish(authorizationText)}
      >
        <Paragraph
          style={{
            textAlign: 'justify',
            textJustify: 'inter-word',
          }}
        >
          <Text>
            To make {paymentNoun} towards the balance on the Mission Lane Visa,
            ending in <strong>{cardLast4}</strong>, you {customerName}, today{' '}
            {today}, authorize Mission Lane to{' '}
            <strong>
              make {lumpSumPayment ? 'an' : 'a recurring'} electronic debit
            </strong>{' '}
            from the bank account ending in {fundingAccountLast4} for the
            following {lumpSumPayment ? 'date and amount' : 'dates and amounts'}
            :
          </Text>
        </Paragraph>
        <Title level={5}>Payment Schedule</Title>
        <PrecoScheduleTable
          data={authorizedSchedule}
          showStatus={false}
          length={6}
        />

        <Divider />
        <Paragraph
          style={{
            textAlign: 'justify',
            textJustify: 'inter-word',
          }}
        >
          To cancel or change {paymentNoun}, call us at least one business day
          before the payment date at (888) 695-8536 from 8:30 AM to 9:00 PM
          Eastern Time, Monday through Friday.
        </Paragraph>

        <Item
          name="efta"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(),
              message: 'Customer must verbally authorize payment to continue.',
            },
          ]}
          style={{ marginBottom: 'var(--spacing-sm)' }}
        >
          <Checkbox>
            Do you authorize [this payment/these payments]? [Wait for response]
          </Checkbox>
        </Item>

        <Alert
          banner
          type="warning"
          showIcon
          title="Customers must verbally acknowledge the above disclosure before moving forward."
          style={{ marginBottom: 'var(--spacing-sm)' }}
        />
        <Text style={{ display: 'block', marginBottom: 'var(--spacing-lg)' }}>
          Thank you, you've successfully authorized {paymentNoun}. We'll send a
          confirmation email to {email}
        </Text>
        {children}
      </Form>
    </QueryBoundary>
  );
};

export default PrecoEFTAForm;
